import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const passwordStrong = password.length >= 10 && /\d/.test(password);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      setError(lang === "fr" ? "Lien invalide." : "Invalid link.");
      return;
    }
    if (!passwordStrong) {
      setError(
        lang === "fr"
          ? "Le mot de passe doit contenir au moins 10 caractères et au moins un chiffre."
          : "Password must be at least 10 characters and contain at least one digit."
      );
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success(t("reset_ok"));
      navigate("/login");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl px-6 py-16" data-testid="reset-page">
      <h1 className="font-serif text-4xl font-semibold text-[#101A42] sm:text-5xl">
        {t("reset_title")}
      </h1>

      <form onSubmit={onSubmit} className="mt-10 space-y-6">
        <div>
          <Label htmlFor="password" className="mb-2 block text-lg font-medium">
            {t("new_password")}
          </Label>
          <Input
            id="password"
            data-testid="reset-password-input"
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-16 rounded-xl border-2 text-lg"
          />
          <p
            className={`mt-2 text-sm ${
              password.length === 0
                ? "text-[#4A4A4A]"
                : passwordStrong
                ? "text-[#A762B1]"
                : "text-[#A762B1]"
            }`}
            data-testid="reset-password-hint"
          >
            {password.length === 0
              ? lang === "fr"
                ? "Au moins 10 caractères, avec au moins un chiffre."
                : "At least 10 characters, with at least one digit."
              : passwordStrong
              ? lang === "fr"
                ? "✓ Mot de passe assez fort."
                : "✓ Password is strong enough."
              : lang === "fr"
              ? "Il manque des caractères ou un chiffre."
              : "Missing characters or a digit."}
          </p>
        </div>

        {error && (
          <div
            data-testid="reset-error"
            className="rounded-xl border-2 border-[#A762B1]/30 bg-[#A762B1]/10 p-4 text-[#6E3978]"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          disabled={submitting}
          data-testid="reset-submit-btn"
          className="min-h-[64px] w-full rounded-full bg-[#A762B1] text-xl font-semibold text-white hover:bg-[#B775C0]"
        >
          {submitting ? "..." : t("reset_cta")}
        </Button>

        <p className="text-center text-lg text-[#4A4A4A]">
          <Link
            to="/login"
            data-testid="reset-back-to-login"
            className="font-semibold text-[#101A42] underline underline-offset-4"
          >
            {t("login")}
          </Link>
        </p>
      </form>
    </div>
  );
}
